import { useEffect, useState } from "react";
import { FiLoader, FiImage, FiSave } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import { useCourse } from "../context/CourseContext";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../utils/apiFetch";

export default function CommunityDetailsEditor() {
  const { course, setCourse } = useCourse();
  const { userId } = useAuth();
  const client = useQueryClient();
  const [name, setName] = useState(course.name || "");
  const [description, setDescription] = useState(course.description || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!file) { setPreview(""); return; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setSaved(false);
    try {
      let thumbnailToken;
      if (file) {
        const response = await apiFetch(`${process.env.API_URL}/api/groups/thumbnail`, { method: "POST", headers: { "Content-Type": file.type }, body: file });
        const data = await response.json();
        if (!response.ok) throw Error(data.message || "Could not upload thumbnail");
        thumbnailToken = data.thumbnailToken;
      }
      const response = await apiFetch(`${process.env.API_URL}/api/groups/${course._id}/details`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, description, ...(thumbnailToken ? { thumbnailToken } : {}) }),
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.message || "Could not save channel details");
      const key = ["account", userId, "group", course.slug];
      await client.cancelQueries({ queryKey: key, exact: true });
      client.setQueryData(key, previous => previous ? { ...previous, workspace: { ...previous.workspace, ...data.details } } : previous);
      setCourse(previous => ({ ...previous, ...data.details }));
      client.invalidateQueries({ queryKey: ["account", userId, "groups"] });
      client.invalidateQueries({ queryKey: ["group-directory"] });
      setName(data.details.name); setDescription(data.details.description); setFile(null); setSaved(true);
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  const inputClass = "mt-2 block w-full rounded-md border border-[var(--app-border)] bg-[var(--app-panel,white)] px-3 py-2 text-sm text-[var(--app-text)]";
  return <form onSubmit={save} className="max-w-xl space-y-5">
    <h1 className="text-xl font-semibold">Channel details</h1>
    <fieldset disabled={busy} className="space-y-5" onChange={() => setSaved(false)}>
      <label className="block text-sm font-semibold">Name<input required maxLength={100} value={name} onChange={event => setName(event.target.value)} className={inputClass} /></label>
      <label className="block text-sm font-semibold">Description<textarea rows={5} maxLength={5000} value={description} onChange={event => setDescription(event.target.value)} className={inputClass} /></label>
      <div className="space-y-3">
        {(preview || course.thumbnail) && <img src={preview || course.thumbnail} alt="Channel thumbnail" className="aspect-video w-full max-w-sm rounded-md object-cover" />}
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[var(--app-border)] px-3 py-2 text-sm font-semibold focus-within:outline-2"><FiImage aria-hidden="true" />Change thumbnail<input aria-label="Change thumbnail" type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={event => {
          const chosen = event.target.files?.[0]; event.target.value = "";
          if (!chosen) return;
          if (!["image/jpeg", "image/png", "image/webp"].includes(chosen.type) || chosen.size > 5 * 1024 * 1024) { setError("Choose a JPG, PNG, or WebP image up to 5 MB."); return; }
          setError(""); setFile(chosen);
        }} /></label>
      </div>
    </fieldset>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {saved && <p role="status" className="text-sm text-green-700">Channel details saved.</p>}
    <button type="submit" disabled={busy || !name.trim()} className="inline-flex min-h-11 items-center gap-2 rounded-md bg-orange-700 px-4 text-sm font-semibold text-white disabled:opacity-50">{busy ? <FiLoader className="animate-spin" aria-label="Saving" /> : <FiSave aria-hidden="true" />}Save changes</button>
  </form>;
}
